// `SvgIconId` is the only import here, and `phosphor-icons.ts` is pure data with no imports of
// its own — so typing an icon field does not give the model layer an edge to Angular Material.
import { SvgIconId } from './shared/icons/phosphor-icons';

export interface APINoDataResponse {
  successful: boolean;
  message: string;
}

export interface APIDataResponse<T> extends APINoDataResponse {
  data: T;
}

/**
 * Machine-readable error codes returned by the API in the error body's `code` field.
 * Mirrors the backend `ErrorCodes` registry (src/exceptions.py). The numeric prefix
 * matches the HTTP status the code is raised with.
 */
export enum ApiErrorCode {
  // 400 Bad Request
  /** Account creation was attempted without accepting the required legal documents. */
  CONSENT_REQUIRED = '400001',
  // 402 Payment Required
  OWN_DEBT = '402001',
  LINKED_DEBT = '402002',
  NO_PAYMENT_METHOD = '402003',
  PAYMENT_VALIDATION_FAILED = '402004',
  CARD_DELETION_BLOCKED = '402005',
  // 403 Forbidden
  USER_BANNED = '403001',
  CARD_BANNED = '403002',
  /** A cooler open was refused because the user owes a legal document. Route to the gate. */
  CONSENT_REQUIRED_AT_OPEN = '403003',
  // 409 Conflict
  /**
   * A newer version of the document was published while it was on screen. Re-render and ask
   * again rather than retrying — the point is that the user has not seen the current text.
   */
  CONSENT_STALE_VERSION = '409001'
}

/** Shape of the parsed error body (HttpErrorResponse.error) returned by the API. */
export interface APIErrorBody {
  successful: false;
  message: string;
  code: string | null;
}

/** Data payload of GET /stores/{storeId}/validation-preview. */
export interface ValidationPreview {
  willValidate: boolean;
  amount: number;
  isPreAuthorization: boolean;
}

/** What the device service already holds, with no library load triggered. */
export interface UserDeviceIdentity {
  /** Server-signed first-party device id, or null until the API has issued one. */
  deviceId: string | null;
  /** OSS FingerprintJS visitor id, if one has been computed this session. Monitoring only. */
  hash: string | null;
  /**
   * Whether this is a private/incognito session. Null means not yet detected or undetectable —
   * distinct from `false`, which is a positive "we checked, it is not private".
   */
  incognito: boolean | null;
}

export enum DeviceType {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  DESKTOP = 'DESKTOP'
}

export enum BrowserType {
  CHROME = 'CHROME',
  SAFARI = 'SAFARI',
  FIREFOX = 'FIREFOX',
  EDGE = 'EDGE',
  OTHER = 'OTHER'
}

export interface OpenData {
  transactionId: string;
  superTransactionId?: string | null;
}

export interface Cooler {
    cameras: string[];
    clientId: string;
    name: string;
    logoUrl: string;
    productsIds: string[];
}

export interface Phone {
  number: string;
  areaCode: string;
}


export enum CardPaymentMethod {
  TRANSBANK = "transbank",
  MERCADOPAGO = "mercadopago2",
  KLAP = "klap",
  DEMO = "demo"
}

export interface KlapPayDebtData {
  orderId: string;
  amountToPay: number;
}

/**
 * Input for the pay-debt dialog. The dialog creates the Klap order itself on submit, because
 * Klap fixes tokenization when the order is created and the "save my card" choice is only
 * known once the user has made it.
 */
export interface KlapPayDebtDialogData {
  transactionId: string;
  amountToPay: number;
  /** Whether the caller is authenticated; false on the public bind-transaction page. */
  requiresToken: boolean;
  /** False for anonymous payers, who have no account to attach a card to. */
  canSaveCard: boolean;
}

/** Result the pay-debt dialog closes with. */
export interface KlapPayDebtDialogResult {
  success: boolean;
  cardSaveRequested?: boolean;
  /** Last four digits of the card that was paid with, to confirm it ended up stored. */
  cardLastDigits?: string;
}

/** Cardholder details optionally sent when starting a Klap card inscription. */
export interface Cardholder {
  name?: string;
  identification?: { number: string; type: 'RUT' | 'OTHER' };
}

/** Request body for POST /users/klap/card. `coolerId` is the legacy store-id name. */
export interface KlapCardInscriptionBody {
  clientId: string;
  cardholder?: Cardholder;
  coolerId?: string;
}

/** Request body for POST /transactions/{id}/klap/pay-debt. */
export interface KlapPayDebtBody {
  saveCard?: boolean;
  cardholder?: Cardholder;
}

export enum UserAdministratedBalances {
  AMIPASS = "amipass",
  EDENRED = "edenred",
  EDENRED_JUNAEB = "edenred_junaeb",
  PLUXEE = "pluxee"
}

/** URL slug identifying an Edenred product on the API. */
export enum EdenredTenantSlug {
  TICKET_RESTAURANT = "ticket-restaurant",
  JUNAEB = "junaeb"
}

/** The balance ids that an Edenred product funds. */
export type EdenredBalanceId =
  | UserAdministratedBalances.EDENRED
  | UserAdministratedBalances.EDENRED_JUNAEB;

/**
 * Maps a wallet balance id onto the Edenred tenant that funds it.
 *
 * Keyed by the exhaustive union rather than by `string`, so adding an Edenred product to
 * `UserAdministratedBalances` fails the build here instead of yielding an `undefined` slug
 * and a request to `/recharges/edenred/undefined` at runtime.
 */
export const EDENRED_BALANCE_TO_SLUG: Record<EdenredBalanceId, EdenredTenantSlug> = {
  [UserAdministratedBalances.EDENRED]: EdenredTenantSlug.TICKET_RESTAURANT,
  [UserAdministratedBalances.EDENRED_JUNAEB]: EdenredTenantSlug.JUNAEB
};


export enum StoreStatus {
  REQUESTED = "REQUESTED",
  OPENED = "OPENED",
  FAILED = "FAILED",
  BUYING = "BUYING",
  TIMEOUT = "TIMEOUT",
  CLOSED = "CLOSED",
}

export enum StoreType {
  SMART = "SMART",
  COFFEE = "COFFEE"
}

export enum UserCardBrand {
  VISA = "visa",
  MASTERCARD = "mastercard",
  AMERICAN_EXPRESS = "amex",
  DINERS = "diners",
  MAGNA = "magna",
  REDCOMPRA = "redcompra",
  PRESTO = "presto",
  UNKNOWN = "unknown"
}

/**
 * Unified card interface that normalizes all card types from different providers.
 * Used to display cards in a consistent format across the application.
 */
export interface UserCard {
  lastDigits: string;
  cardType: PaymentType;
  brand: UserCardBrand;
  provider: CardPaymentMethod;
  providerSpecificData?: Record<string, unknown>;
}

export enum AccessCredentialsStatus {
  ACTIVE = 'ACTIVE',
  REVOKED = 'REVOKED',
  EXPIRED = 'EXPIRED'
}

/**
 * Metadata for a user's store-access credential. Never contains the
 * plaintext secret; that is only emitted once, when the credential is
 * created (e.g. when minting a Google Wallet pass).
 */
export interface AccessCredentialsMetadata {
  id: string;
  userId: string;
  status: AccessCredentialsStatus;
  createdAt: number;
  expiresAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

/**
 * Response payload for the "Add to Google Wallet" endpoint. `saveUrl` is the
 * Google "Save to Wallet" link the client must open/redirect to.
 */
export interface GoogleWalletSaveLink {
  saveUrl: string;
  metadata: AccessCredentialsMetadata;
}

export interface Balance {
  active: boolean;
  balance: number | {[productGroup: string]: number};
  rechargeTimestamp?: number; // Legacy
  name?: string;
  type?: BalanceType;
  expiresAt: number | null;
  benefitType?: BalanceBenefitType;
  inSchedule?: boolean;
  schedule?: BalanceSchedule;
  scheduleTimezone?: string;
  identifierType?: BalanceIdentifierType;
}

export interface BalanceSchedule {
    startScheduleHour: number;
    startScheduleMinute: number;
    endScheduleHour: number;
    endScheduleMinute: number;
    scheduleDays: number[] | null;
}

export enum BalanceType {
    BALANCE = "BALANCE",
    MEAL = "MEAL"
}

export enum BalanceBenefitType {
    CURRENCY = "CURRENCY",
    POINTS = "POINTS",
    MIX = "MIX"
}

export enum BalanceIdentifierType {
    EMAIL = "EMAIL",
    RUT = "RUT",
    CUSTOM = "CUSTOM"
}

/**
 * Balance entry with its key identifier.
 * Used for iterating over balances in templates.
 */
export interface BalanceWithKey {
  key: string;
  balance: Balance;
}

/** Unified card format - available when normalize=true is passed to getUserPaymentMethods */
export interface UserPaymentMethods {
  balance?: {[balanceId: string]: Balance};
  cards?: UserCard[];
}

export interface User {
    active: boolean;
    email?: string;
    phone?: {
      number: string;
      areaCode: string;
    };
    id: string;
    name?: string;
    paymentMethods: UserPaymentMethods;
    transactionsCount: number;
    Rut?: string;
    runChecked?: boolean;
}

export interface UserPersonalData {
    id: string;
    name: string;
    email: string;
    phone?: Phone;
    Rut?: string;
    runChecked: boolean;
}

export interface CatalogData {
  products: Product[];
  productsPacks: ProductsPack[];
}

export interface AmipassInitInput {
  amount: number;
  urlExito: string;
  urlFracaso: string;
}

export interface EdenredRechargeInput {
  amount: number;
}

/**
 * A linked (or linkable) Edenred account, as returned by GET /wallets/edenred.
 *
 * `minAmount`/`maxAmount` are the Chilean per-product limits. They come from the API so the
 * regulatory values have one owner instead of being restated here.
 */
export interface EdenredAccount {
  tenant: string;
  slug: EdenredTenantSlug;
  displayName: string;
  balanceId: string;
  linked: boolean;
  minAmount: number;
  maxAmount?: number | null;
  username?: string;
  linkedAt?: number;
  availableAmount?: number;
}

export interface EdenredLinkInitResult {
  tenant: string;
  slug: EdenredTenantSlug;
  authorizeUrl: string;
}

export interface EdenredRechargeResult {
  rechargeId: string;
  tenant: string;
  slug: EdenredTenantSlug;
  balanceId: string;
  amount: number;
  status: string;
  authorizationId?: string;
  captureId?: string;
}

/**
 * What the confirmation page is told about a completed top-up.
 *
 * Handed over as Angular navigation state, never as query params: the amount is money the app
 * claims to have charged, and a URL-visible copy of it can be edited into a convincing forged
 * receipt. `rechargeId` is what marks the state as coming from a real charge.
 */
export interface EdenredRechargeResultState {
  rechargeId: string;
  amount: number;
  storeId?: string;
}

/**
 * A recharge stashed while the user completes the Edenred login, so the flow can resume
 * on the link callback page instead of making them enter the amount twice.
 */
export interface PendingEdenredRecharge {
  slug: EdenredTenantSlug;
  amount: number;
  storeId?: string;
}

export enum PaymentType {
    DEMO = "Demo",
    BALANCE = "Saldo",
    DEBIT = "Débito",
    CREDIT = "Crédito",
    PREPAID = "Prepago",
    UNKNOWN = "Desconocido"
}

export enum PaymentResponseStatus {
    AUTHORIZED = "AUTHORIZED",
    REJECTED = "REJECTED",
    REFUNDED = "REFUNDED"
}

export enum TransactionStatusTypes {
    AUTHORIZED = "AUTHORIZED",
    AUTHORIZED_DEMO = "AUTHORIZED_DEMO",
    AUTHORIZED_ZERO = "AUTHORIZED_ZERO",
    ALREADY_AUTHORIZED = "ALREADY_AUTHORIZED",
    REJECTED = "REJECTED",
    REFUNDED = "REFUNDED",
    COULDNT_CHARGE = "COULDNT_CHARGE",
    PENDING = "PENDING",
    BLOCKED = "BLOCKED"
}

/** Request body for PATCH /users/transbank/card. `coolerId` is the legacy store-id name. */
export interface TransbankCardInscriptionBody {
  tbk_token: string;
  username: string;
  clientId: string;
  coolerId?: string;
}

// TODO: To be removed once firestoreService is replaced by SSE
export interface TransbankAuthorizedResponse {
    accounting_date: string;
    buy_order: string;
    card_detail: {
        card_number: string;
    };
    details: {
        amount: number;
        authorization_code: string;
        buy_order: string;
        commerce_code: string,
        installments_number: number;
        payment_type_code: string;
        response_code: number;
        status: string;
    }[];
    transaction_date: string;
}

// TODO: To be removed once firestoreService is replaced by SSE
export interface MercadoPagoAuthorizedResponse {
    id: number;
    date_created: string;
    date_approved: string;
    date_last_updated: string;
    date_of_expiration: string;
    money_release_date: string;
    operation_type: string;
    issuer_id: string;
    payment_method_id: string;
    payment_type_id: string;
    status: string;
    status_detail: string;
    currency_id: string;
    description: string;
    live_mode: boolean;
    sponsor_id: string;
    authorization_code: string;
    money_release_schema: string;
    taxes_amount: number;
    counter_currency: string;
    shipping_amount: number;
    pos_id: string;
    store_id: string;
    collector_id: number;
    payer: {[key: string]: any}
    metadata: {[key: string]: any}
    additional_info: {[key: string]: any}
    external_reference: string;
    transaction_amount: number;
    transaction_amount_refunded: number;
    coupon_amount: number;
    differential_pricing_id: string;
    deduction_schema: string;
    transaction_details: {[key: string]: any}
    fee_details: any[];
    captured: boolean;
    binary_mode: boolean;
    call_for_authorize_id: string;
    statement_descriptor: string;
    installments: number;
    card: {[key: string]: any}
    notification_url: string;
    processing_mode: string;
    merchant_account_id: string;
    acquirer: string;
    merchant_number: string;
    point_of_interaction: {[key: string]: any}
}

// TODO: To be removed once firestoreService is replaced by SSE
export interface TransbankRefundedResponse {
    authorization_code: string;
    authorization_date: string;
    balance: number;
    buy_order: string;
    nullified_amount: number;
    response_code: number;
    type: string;
}

// TODO: To be removed once firestoreService is replaced by SSE
export interface MercadoPagoRefundedResponse {
    id: number;
    payment_id: number;
    amount: number;
    metadata: any[];
    source: string[];
    date_created: string;
    unique_sequence_number: string;
    refund_mode: string;
    adjustment_amount: number;
    status: string;
    reason: string;
    label: any[];
    partition_details: any[];
}

// TODO: To be removed once firestoreService is replaced by SSE
export interface IntegrationTransaction {
    amount?: number;
    clientId: string;
    id: string;
    paymentMethod?: string;
    paymentResponses?: {
        authorized : TransbankAuthorizedResponse[] | MercadoPagoAuthorizedResponse[],
        refunded: TransbankRefundedResponse[] | MercadoPagoRefundedResponse[],
        rejected: any[]
    };
    requestTimestamp: number;
    results?: TransactionResults;
    status?: string;
    store: Store;
    storeId: string;
    storeStatus: StoreStatus;
    rating?: number;
    ratingComments?: string;
    userId: string;
    collector?: {
      id: string;
      name: string;
      address: string;
    }
}

export interface TransactionSummary {
  id: string;
  storeName: string;
  requestTimestamp: number;
  amount: number;
  /** How much of `amount` was collected. A REJECTED transaction owes the difference. */
  chargedAmount?: number;
  status: TransactionStatusTypes;
  balanceConfig?: BalanceConfig;
  userId?: string | null;
}

export interface TransactionResults {
  amount: number;
  products: Product[];
  productsPacksDiscount: number;
  couponDiscount: number;
}

export interface CachedTransactionDetails {
  results?: TransactionResults;
  payments?: TransactionPayment[];
  rating?: TransactionRating;
  loadingState: {
    results: 'idle' | 'loading' | 'loaded' | 'error';
    payments: 'idle' | 'loading' | 'loaded' | 'error';
    rating: 'idle' | 'loading' | 'loaded' | 'error';
  };
}

export interface TransactionPayment {
  id: string;
  dateIsoformat: string;
  status: PaymentResponseStatus;
  amount: number;
  authorizationCode: string | null;
  terminalId: string | null;
  type: PaymentType | null;
  collector: {
    id: string;
    name: string;
    address: string;
  } | null;
}

export interface TransactionRating {
  rating: number;
  comments: string;
}

export interface TwilioVerifyResponseData {
  customToken: string;
}

export interface PhoneEvent {
  phoneNumber: string;
  dialCode: string;
  completePhoneNumber: string;
}

// TODO: This could simply be a list of strings or an enum
export interface AcceptedPaymentMethods {
  mercadopago2?: {
    requireSecurityCode: {
      debit_card: boolean;
      credit_card: boolean;
    }
  };
  transbank?: {[key: string]: any};
}

export interface BalanceRechargeSelectionData {
  availableMethods: UserAdministratedBalances[];
  origin: string;
  storeId?: string;
}

export interface LastStoreEntry {
  id: string;
  name: string;
  lastTransactionTimestamp: number;
}

export interface BalanceConfig {
  allowedBenefitTypes: BalanceBenefitType[];
  defaultCurrency: "CLP";
}

export interface Store {
    active: boolean;
    clientId?: string;
    storeId: string;
    marketplaceId?: string;
    name: string;
    logoUrl?: string;
    logoUrlDark?: string;
    logoUrlWhite?: string;
    acceptedPaymentMethods: AcceptedPaymentMethods;
    createAmount?: number;
    validationAmount?: number;
    creditFirstValidationAmount?: number;
    debitFirstValidationAmount?: number;
    openType?: string;
    storeType?: StoreType;
    requiresRunInscription?: boolean;
    balanceConfig?: BalanceConfig;
}

export interface UpdateUserResponseData {
  user: User;
  customToken: string;
}

export interface Coupons {
  [key:string] : Coupon
}

export interface Coupon {
    category: string;
    description: string;
    discount: number;
    endTimestamp: number;
    id: string;
    limitPerUser: number;
    maxDiscount?: number;
    minAmount?: number;
    startTimestamp: number;
    stores?: string[];
    timesUsedByUser: number;
    type: string;
}

export interface Product {
  category: string;
  company: string;
  features?: string;
  size?:string;
  quantity: number;
  id: string;
  image_url: string;
  thumbnail_url: string;
  name: string;
  price: number;
  discount?: Discount;
}

export interface Discount {
  endTimestamp: number;
  startTimestamp: number;
  type: string;
  value: number;
}

export interface ProductsPack {
  id: string
  name: string;
  price: number;
  products: { [productId: string]: {
    quantity: number;
    price: number;
    name: string;
    }
  };
  startTimestamp: number;
  endTimestamp: number;
  currentUsage?: number;
  usageLimit: number | null;
  imageUrl: string;
}

export interface FAQ {
  answer: string;
  category: string;
  clientId: string;
  createdAt: number;
  id: string;
  question: string;
}

export interface LoginUrlResponseData {
  url: string;
  code_verifier: string;
  hashed_code_verifier: string;
  state: string;
  scope: string;
}

export enum LegalDocumentType {
  TNC = 'tnc',
  PRIVACY_POLICY = 'privacy-policy',
}

/** `id` is the last-update date in `YYYY-MM-DD` format, and is the version consent is recorded against. */
export interface LegalDocument {
  id: string;
  title: string;
  sections: DocumentSection[];
  /**
   * The verbatim markdown published from the legal-docs repo. Preferred for rendering, and the
   * evidence of what the user was shown. Absent on versions entered by hand in the console.
   */
  markdown?: string;
  /** sha256 of `markdown`, pinning the exact content. */
  checksum?: string;
}

export interface DocumentSection {
  title: string;
  content: {
    "type": "p" | "ul" | "ol" | "h3";
    "text"?: string;
    "items"?: string[];
  }[];
}

/** What a client needs to know about a document type without downloading its body. */
export interface LegalDocumentRequirement {
  documentType: LegalDocumentType;
  currentVersion: string | null;
  requiresReacceptance: boolean;
  changeSummary: string | null;
}

/** Optional processing purposes the user grants or refuses independently. */
export enum ConsentPurpose {
  MARKETING_COMMS = 'marketing_comms',
  COOKIES_ANALYTICS = 'cookies_analytics',
  COOKIES_MARKETING = 'cookies_marketing',
}

/** Where a consent decision was captured. Part of the evidence, not a label. */
export enum ConsentChannel {
  SIGNUP = 'signup',
  REACCEPTANCE_GATE = 'reacceptance_gate',
  COOKIE_BANNER = 'cookie_banner',
  SETTINGS = 'settings',
  MARKETING_PROMPT = 'marketing_prompt',
}

/** A document version the user must accept before continuing. */
export interface PendingDocument {
  documentType: LegalDocumentType;
  version: string;
  requiresReacceptance: boolean;
  changeSummary: string | null;
}

export interface DocumentAcceptance {
  documentType: LegalDocumentType;
  version: string;
  lang?: string;
}

export interface DocumentConsentState {
  version: string;
  lang?: string;
  /** Unix **seconds**, as the API records them — multiply by 1000 before any `Date` or `DatePipe`. */
  acceptedAt: number;
}

export interface PurposeConsentState {
  granted: boolean;
  /** Unix **seconds**, as the API records them — multiply by 1000 before any `Date` or `DatePipe`. */
  at: number;
}

/**
 * Enforcement timing, resolved server-side.
 *
 * For copy only. This build ships the acceptance UI, so it is gated whatever the mode says — the
 * guard must never consult this, and must never compute the phase from the device clock.
 */
export interface ConsentEnforcementInfo {
  mode: 'soft' | 'hard';
  at: string | null;
}

export interface ConsentState {
  userId: string;
  documents: Partial<Record<LegalDocumentType, DocumentConsentState>>;
  purposes: Partial<Record<ConsentPurpose, PurposeConsentState>>;
  pendingDocuments: PendingDocument[];
  enforcement?: ConsentEnforcementInfo;
}

/** One immutable entry in the user's consent history. Read-only for clients. */
export interface ConsentEvent {
  id: string;
  /** Unix **seconds**, as the API records them — multiply by 1000 before any `Date` or `DatePipe`. */
  at: number;
  kind: string;
  channel: ConsentChannel;
  documentType?: LegalDocumentType;
  version?: string;
  lang?: string;
  purpose?: ConsentPurpose;
  granted?: boolean;
}

/** The cookie categories the banner offers. `necessary` is always on and has no toggle. */
export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
}

export interface CountryCode {
  name: string;
  dial_code: string;
  code: string;
  src: string;
}

export interface SideBarLink {
  svgIcon: SvgIconId;
  title: string;
  route: string;
  action: () => void;
}

export enum OTPChannels {
  SMS = "sms",
  WHATSAPP = "whatsapp",
  EMAIL = "email"
}

export enum ReCaptchaV3ExecutionCodes {
  EXISTS = "EXISTS",
  LOGIN = "LOGIN"
}

export enum LoggedUserIssueCategories {
  DOOR_OPEN_ERROR = "DOOR_OPEN_ERROR",
  BAD_PRODUCT = "BAD_PRODUCT",
  BAD_CHARGE = "BAD_CHARGE",
  OTHER = "OTHER",
  SUGGESTIONS = "SUGGESTIONS",
}

export enum UnloggedUserIssueCategories {
  EMAIL_ERROR = "EMAIL_ERROR",
  SMS_ERROR = "SMS_ERROR",
  OTHER = "OTHER",
}

export type UserIssueCategories = LoggedUserIssueCategories | UnloggedUserIssueCategories;

// TODO: Translate labels with i18n
export const UserIssueCategoriesLabels: Record<UserIssueCategories, string> = {
    [LoggedUserIssueCategories.DOOR_OPEN_ERROR]: "Error de apertura",
    [LoggedUserIssueCategories.BAD_PRODUCT]: "Producto malo",
    [LoggedUserIssueCategories.BAD_CHARGE]: "Cobro incorrecto",
    [LoggedUserIssueCategories.OTHER]: "Otro",
    [LoggedUserIssueCategories.SUGGESTIONS]: "Sugerencia",
    [UnloggedUserIssueCategories.EMAIL_ERROR]: "Problemas con mi correo",
    [UnloggedUserIssueCategories.SMS_ERROR]: "Problemas con mi teléfono",
};

/**
 * Phosphor icons, registered with `MatIconRegistry` in `app.component.ts`.
 *
 * Each one names the thing the category is about rather than a reaction to it: the cooler door,
 * the charge, the phone. The two that did not — a raised palm for a door that would not open and
 * a thumbs-down for a product in bad condition — were what made the list hard to scan.
 */
export const UserIssueCategoriesIcons: Record<UserIssueCategories, SvgIconId> = {
    [LoggedUserIssueCategories.DOOR_OPEN_ERROR]: "ph-regular-door-open",
    [LoggedUserIssueCategories.BAD_PRODUCT]: "ph-regular-seal-warning",
    [LoggedUserIssueCategories.BAD_CHARGE]: "ph-regular-currency-circle-dollar",
    [LoggedUserIssueCategories.OTHER]: "ph-regular-dots-three-circle",
    [LoggedUserIssueCategories.SUGGESTIONS]: "ph-regular-lightbulb",
    [UnloggedUserIssueCategories.EMAIL_ERROR]: "ph-regular-envelope",
    [UnloggedUserIssueCategories.SMS_ERROR]: "ph-regular-device-mobile",
};

export interface ImageFile {
  file: File;
  preview: string;
}

/** A file picked for a help issue reply, carrying what its preview row needs to render. */
export interface SelectedAttachment {
  file: File;
  /** Object URL for an image or a video; null for a file rendered as a named row. */
  previewUrl: string | null;
  isImage: boolean;
  isVideo: boolean;
  sizeLabel: string;
}

/** Families of file a user may attach to a help issue or one of its responses. */
export enum AttachmentKind {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  PDF = "PDF",
  SPREADSHEET = "SPREADSHEET",
}

export interface AttachmentKindSpec {
  /** Matched against `File.type` with `startsWith`, so a trailing `/` covers a whole family. */
  mimePrefixes: string[];
  /**
   * Matched against the end of the lower-cased file name. A browser reports an empty or
   * vendor-specific `File.type` for CSV and for Office documents depending on the platform and
   * on which applications are installed, so the extension is the only dependable signal there.
   */
  extensions: string[];
}

export const ATTACHMENT_KIND_SPECS: Record<AttachmentKind, AttachmentKindSpec> = {
  [AttachmentKind.IMAGE]: {
    mimePrefixes: ["image/"],
    extensions: [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic", ".heif"],
  },
  [AttachmentKind.VIDEO]: {
    mimePrefixes: ["video/"],
    extensions: [".mp4", ".mov", ".webm", ".m4v", ".3gp"],
  },
  [AttachmentKind.PDF]: {
    mimePrefixes: ["application/pdf"],
    extensions: [".pdf"],
  },
  [AttachmentKind.SPREADSHEET]: {
    mimePrefixes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml",
      "application/vnd.ms-excel",
      "application/vnd.oasis.opendocument.spreadsheet",
      "text/csv",
    ],
    extensions: [".xlsx", ".xls", ".xlsm", ".csv", ".ods"],
  },
};

export interface ContactFormData {
  category: UserIssueCategories;
  message: string;
  images: File[];
  email?: string;
  transactionId?: string;
}

export interface UserIssue {
  id: string;
  createdAt: number;
  userId?: string;
  storeId?: string;
  transactionId?: string;
  message: string;
  status: UserIssueStatus;
  imagesUrls?: string[];
  category: UserIssueCategories;
}

export enum UserIssueStatus {
  OPEN = "OPEN",
  DOING = "DOING",
  DONE = "DONE",
  CLOSED = "CLOSED",
}

export const UserIssueStatusLabels: Record<UserIssueStatus, string> = {
  [UserIssueStatus.OPEN]: "Abierto",
  [UserIssueStatus.DOING]: "En proceso",
  [UserIssueStatus.DONE]: "Resuelto",
  [UserIssueStatus.CLOSED]: "Cerrado",
}

/**
 * Chip chrome per status. The DS draws each status on its `tertiary` tint outlined in the
 * matching `primary`; the label stays `Text/primary` throughout, so only the frame carries the
 * meaning. A closed issue has no status colour of its own and takes the neutral surface.
 */
export const UserIssueStatusColors: Record<UserIssueStatus, string> = {
  [UserIssueStatus.OPEN]: "bg-status-warning-bg border-status-warning",
  [UserIssueStatus.DOING]: "bg-status-info-bg border-status-info",
  [UserIssueStatus.DONE]: "bg-status-success-bg border-status-success",
  [UserIssueStatus.CLOSED]: "bg-default-background border-primary",
}

export const UserIssueStatusIcons: Record<UserIssueStatus, SvgIconId> = {
  [UserIssueStatus.OPEN]: "ph-regular-sign-in",
  [UserIssueStatus.DOING]: "ph-regular-clock-countdown",
  [UserIssueStatus.DONE]: "ph-regular-check",
  [UserIssueStatus.CLOSED]: "ph-regular-handshake",
}

export enum UserIssueResponseOrigins {
  USER = "USER",
  SUPPORT = "SUPPORT"
}
export interface UserIssueResponse {
  origin: string;
  message: string;
  imagesUrls: string[];
  timestamp: number;
}

export enum VerifyEmailMethods {
  VERIFY = "verify",
  BIND = "bind"
}

export interface ProductGroupProduct {
  id: string;
  name: string;
  image_url: string;
}

export interface ProductGroupDetails {
  name: string;
  products: ProductGroupProduct[];
}

export interface ProductsGroupsDetailsResponse {
  [productsGroupId: string]: ProductGroupDetails;
}
